import { BillingRequest } from '@gocardless/react-dropin';
import React from 'react';
import { Button, Modal, ModalTitle, Row, Stack } from 'react-bootstrap';
import { IAccreditationApplication } from 'interfaces/IAccreditationApplication';
import { GoCardlessButton } from '../../Form/PaymentButton/GoCardlessButton';
import { LoadingSpinner } from '../../LoadingSpinner';
import useAccreditationPaymentModal from './useAccreditationPaymentModal';
import { PayPalButton } from '../../Form/PaymentButton/PayPalButton';
import { LoadingError } from '../../LoadingError';

interface Props {
    onPaymentSuccess: () => void;
    onPaymentError: () => void;
    application: IAccreditationApplication;
}

const paypalCartDefault = {
    quantity: '1',
    type: 'accreditation',
};

const AccreditationPaymentModal: React.FC<Props> = ({
    onPaymentSuccess,
    onPaymentError,
    application,
}) => {
    const { models, operations } = useAccreditationPaymentModal(application);
    const {
        handleShow,
        handleClose,
        handleGoCardlessPaymentSuccess,
        handlePayPalPaymentSuccess,
        handleExit,
    } = operations;
    const { show, flowId, envId, error, loading } = models;

    const handleGoCardlessSuccess = (billingRequest: BillingRequest) => {
        return handleGoCardlessPaymentSuccess(billingRequest)
            .then(() => {
                onPaymentSuccess();
            })
            .catch((error) => {
                console.error('An error occurred during payment', error);
                onPaymentError();
            });
    };

    const handlePayPalSuccess = (responseData: any) => {
        return handlePayPalPaymentSuccess(responseData)
            .then(() => {
                onPaymentSuccess();
            })
            .catch((error) => {
                console.error('An error occurred during payment', error);
                onPaymentError();
            });
    };

    if (error) return <LoadingError />;

    if (!flowId && loading) return <LoadingSpinner />;

    const paypalCart = {
        items: [
            {
                ...paypalCartDefault,
                id: application.accreditationApplicationId,
            },
        ],
    };

    return (
        <>
            <Button className="btn btn-primary" onClick={handleShow}>
                Continue to payment
            </Button>
            {show && (
                <Modal show={show} onHide={handleClose} size={'lg'}>
                    <Modal.Header closeButton className={'border-0 align-items-start'}>
                        <ModalTitle>
                            Next, you'll need to pay your &pound;
                            {application.isRenewal
                                ? application.accreditationType.renewalPrice + ' reaccreditation '
                                : application.accreditationType.price + ' accreditation '}
                            fee to submit your application.
                        </ModalTitle>
                    </Modal.Header>
                    <Modal.Body>
                        <Row>
                            <div>
                                By submitting your application you agree to the following:
                                <ul>
                                    <li>
                                        Once you've submitted your form you'll be unable to make any
                                        changes.
                                    </li>
                                    <li>Your accreditation fee is non-refundable.</li>
                                    <li>
                                        Your accreditation will last for 5 years, after which, to
                                        continue being EMDR Europe accredited, you'll need to apply
                                        for re-accreditation.
                                    </li>
                                </ul>
                            </div>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="justify-content-center">
                        <Stack direction="horizontal" className="mx-auto" gap={4}>
                            <Button variant="outline-secondary" onClick={handleClose}>
                                Close
                            </Button>
                            <GoCardlessButton
                                disabled={loading}
                                billingRequestFlowID={flowId as string}
                                environment={envId}
                                onExit={handleExit}
                                onSuccess={handleGoCardlessSuccess}
                                label={<>Checkout</>}
                            />
                            <PayPalButton
                                label={''}
                                cart={paypalCart}
                                environment={envId}
                                disabled={loading}
                                onSuccess={handlePayPalSuccess}
                                onExit={handleExit}
                                data-testid="paypal-button"
                            />
                        </Stack>
                    </Modal.Footer>
                </Modal>
            )}
        </>
    );
};

export default AccreditationPaymentModal;
